import React, { useMemo } from 'react'
import type { QuoteItemSection, RenderableItem } from '@paintscout/util/builder'
import { getFeature, getQuoteOptions } from '@paintscout/util/builder'
import { Typography, HtmlContent, makeStyles, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { OptionsApprovalButton, useClientOptions, useUser } from '@paintscout/ui-business'
import classNames from 'classnames'
import { translateStructuredWorkOrderContent } from '../../Quote/views/WorkOrderView/WorkOrderCard/translateStructuredWorkOrderContent'
import { useTranslatedNoteContent } from '../../Quote/views/WorkOrderView/useTranslatedNoteContent'
import { useQuote } from '../../context'
import NameColumn from './NameColumn'
import SplitAreaPrice from './SplitAreaPrice'
import { shouldShowSubItemAmount } from './shouldShowSubItemAmount'
import { joinTranslatedDescription, splitDescriptionForTranslation } from './splitDescriptionForTranslation'

const JUSTIFY_SPACE_BETWEEN = 'space-between'

// Positions in ItemColumn's translationInputs array.
const NAME_INPUT = 0
const DESCRIPTION_INPUT = 1
const CLIENT_NOTE_INPUT = 2
const SUB_ITEM_INPUTS_START = 3

const useStyles = makeStyles<Theme, { hasOptionsApproval?: boolean; compact?: boolean }>((theme: Theme) => ({
  root: {},
  // Matches the Work Order's own type scale (WorkOrderCardNext's name/subLine/rowValue are all
  // 14-15px) — the shared row otherwise defaults to `body1`, which reads noticeably larger there
  // (ENG-1850). Cascades into NameColumn's/HtmlContent's plain text; Typography's own `value` below
  // needs its own override since a variant sets an explicit font-size that inheritance can't reach.
  compactText: {
    fontSize: theme.typography.pxToRem(14)
  },
  avoidPageBreak: {
    '@media print': {
      pageBreakInside: 'avoid'
    }
  },
  value: ({ compact }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    whiteSpace: 'nowrap',
    marginLeft: theme.spacing(),
    wordBreak: 'keep-all',
    ...(compact ? { fontSize: theme.typography.pxToRem(14) } : {}),
    [theme.breakpoints.down('md')]: {
      display: 'block'
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0
    },
    '& span': {
      fontWeight: theme.typography.fontWeightMedium
    }
  }),
  stackedItemName: ({ compact }) => ({
    display: 'flex',
    flexDirection: 'row',
    // `start` box-aligns the name and value, but the value's Typography variant carries a taller
    // line-height than the name's plain text, so their tops line up while the glyphs don't —
    // `baseline` (compact/Work Order only, to keep the rest of the app unchanged) fixes that (ENG-1850).
    alignItems: compact ? 'baseline' : 'start',
    justifyContent: JUSTIFY_SPACE_BETWEEN,
    [theme.breakpoints.down('sm')]: {
      // flexDirection: 'column'
    },
    '@media print': {
      flexDirection: 'row'
    }
  }),
  firstLine: ({ compact }) => ({
    display: 'flex',
    alignItems: compact ? 'baseline' : undefined,
    justifyContent: JUSTIFY_SPACE_BETWEEN,
    wordBreak: 'break-word'
  }),
  descriptionWrapper: {
    display: 'flex',
    justifyContent: JUSTIFY_SPACE_BETWEEN
  },
  description: ({ hasOptionsApproval }) => {
    const nonOptionsApprovalStyles = {
      wordBreak: 'break-word',
      [theme.breakpoints.up('md')]: {
        maxWidth: 600
      },
      ['@media print']: {
        maxWidth: 600
      }
    }

    if (!hasOptionsApproval) {
      return nonOptionsApprovalStyles
    }

    const optionsApprovalStyles = {
      [theme.breakpoints.down('md')]: {
        maxWidth: 'calc(100% - 128px)'
      },
      [theme.breakpoints.down('sm')]: {
        maxWidth: 'calc(100% - 94px)'
      }
    }

    return {
      ...nonOptionsApprovalStyles,
      ...optionsApprovalStyles
    }
  },

  priceWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end'
  },
  justifyEnd: {
    display: 'flex',
    justifyContent: 'end'
  }
}))

export interface ItemColumnProps {
  item: RenderableItem
  showAmount?: boolean
  consumer: string
  isEditable: boolean
  section: QuoteItemSection
  /** Shrinks text to the Work Order's smaller type scale (matches WorkOrderCardNext). */
  compact?: boolean
}

function ItemColumn({ item, isEditable, showAmount, consumer, section, compact }: ItemColumnProps) {
  const { options } = useClientOptions()
  const { quote, isPresentation, onItemAction } = useQuote()
  const { __ } = useTranslation()
  const { value, name, description } = item
  const { isSuperadmin, isSupport } = useUser()

  const hasOptionsApproval = options?.options?.optionsApproval?.enabled

  const { splitAreaPrice, showDetailedBreakdown, stackSubstrates } = getQuoteOptions({ quote, options })

  const hasName = !!name
  const hasDescription =
    (description.useCustom && !!description.custom) || (!description.useCustom && !!description.default)

  const hasSubItems = item?.subItems && item?.subItems?.length > 0
  const showSubItemAmount = shouldShowSubItemAmount(item)
  const showSplitAreaPrice = splitAreaPrice && item?.type === 'area' && consumer === 'customer'
  const backupPrice = item?.additionalFields?.price + item?.additionalFields?.materials
  const showOptionsApprovalButton =
    hasOptionsApproval &&
    isPresentation &&
    section.includes('options') &&
    !quote?.is_invoice &&
    quote?.status?.value !== 'accepted' &&
    !(quote?.calculationLock && (!isSuperadmin || !isSupport))
  const classes = useStyles({ hasOptionsApproval: hasOptionsApproval && showOptionsApprovalButton, compact })

  const handleAddOption = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()

    if (!onItemAction) {
      return
    }

    if (section === 'added-options') {
      onItemAction('make-option', 'added-options', [item.key])
    } else {
      onItemAction('move-to-added-options', 'options', [item.key])
    }
  }

  // A little confusing, but this prevents the button appearing twice if two of the conditions are met.
  const getOptionsApprovalPlacement = (): 'first' | 'second' | 'third' | 'fourth' | 'fifth' => {
    if (!showOptionsApprovalButton) {
      return null
    }

    if (showDetailedBreakdown) {
      return 'first'
    }

    if ((!!value || !hasSubItems) && showAmount && !hasSubItems && !(hasName && hasDescription)) {
      return 'second'
    }

    if (hasSubItems && (!(hasName && hasDescription) || stackSubstrates)) {
      return 'third'
    }

    if (hasName && hasDescription && !showSplitAreaPrice) {
      return 'fourth'
    }

    if (showSplitAreaPrice) {
      return 'fifth'
    }
  }

  const optionsApprovalPlacement = getOptionsApprovalPlacement()
  const noPdfPageBreak = getFeature({ options, path: 'quotes.noPdfPageBreak' })
  const rawDescription = String(description.useCustom ? description.custom ?? '' : description.default ?? '')
  // Translations are stored per field, so look up the description and client note separately.
  const [descriptionPart, clientNotePart] = splitDescriptionForTranslation(
    rawDescription,
    consumer === 'customer' && !description.useCustom ? item?.additionalFields?.clientNote : null
  )
  const subItems = useMemo(() => item?.subItems ?? [], [item?.subItems])
  // translationInputs layout: [name, description, clientNote, ...per sub-item (name, description)]
  const translationInputs = useMemo(
    () => [
      String(name ?? ''),
      descriptionPart,
      clientNotePart,
      ...subItems.flatMap((subItem) => [String(subItem.name ?? ''), String(subItem.description ?? '')])
    ],
    [name, descriptionPart, clientNotePart, subItems]
  )
  const [translatedInputs] = useTranslatedNoteContent(translationInputs)
  const translatedName = translateStructuredWorkOrderContent(translatedInputs[NAME_INPUT] ?? String(name ?? ''), __)
  const translatedDescription = translateStructuredWorkOrderContent(
    joinTranslatedDescription(
      translatedInputs[DESCRIPTION_INPUT] ?? descriptionPart,
      translatedInputs[CLIENT_NOTE_INPUT] ?? clientNotePart
    ),
    __
  )
  const translatedSubItems = useMemo(
    () =>
      subItems.map((subItem, index) => {
        const translationOffset = SUB_ITEM_INPUTS_START + index * 2
        return {
          ...subItem,
          translatedName: translateStructuredWorkOrderContent(
            translatedInputs[translationOffset] ?? String(subItem.name ?? ''),
            __
          ),
          translatedDescription: translateStructuredWorkOrderContent(
            translatedInputs[translationOffset + 1] ?? subItem.description ?? '',
            __
          )
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subItems, translatedInputs, __]
  )

  return (
    <div
      className={classNames({
        [classes.root]: true,
        [classes.avoidPageBreak]: !noPdfPageBreak,
        [classes.compactText]: compact
      })}
    >
      <div className={classes.stackedItemName}>
        <NameColumn item={{ ...item, name: translatedName }} isEditable={isEditable} />
        {optionsApprovalPlacement === 'first' && <OptionsApprovalButton onClick={handleAddOption} section={section} />}
        {(!!value || !hasSubItems) &&
          showAmount &&
          (showSplitAreaPrice ? (
            <SplitAreaPrice areaKey={item.key} section={section} />
          ) : (
            <div className={classes.priceWrapper}>
              <Typography
                value={(item?.value === '' && !hasSubItems ? backupPrice : item?.value) as unknown as number}
                component="span"
                showUnits={consumer === 'crew' ? false : true}
                format={consumer === 'crew' ? 'hours' : 'price'}
                variant={'body1'}
                classes={{ root: classes.value }}
              />
              {optionsApprovalPlacement === 'second' && (
                <OptionsApprovalButton onClick={handleAddOption} section={section} padTop />
              )}
            </div>
          ))}
      </div>

      {hasSubItems && (
        <div>
          {translatedSubItems.map((subItem, index) => (
            <div key={index}>
              <div className={classes.firstLine}>
                <HtmlContent content={subItem.name ? subItem.translatedName : subItem.translatedDescription} />
                {subItem.value && showSubItemAmount && (
                  <Typography
                    value={showAmount ? (subItem.value as unknown as number) : null}
                    format={consumer === 'crew' ? 'hours' : 'price'}
                    showUnits={consumer === 'crew' ? false : true}
                    variant={'body1'}
                    classes={{ root: classes.value }}
                  />
                )}
                {optionsApprovalPlacement === 'third' && index === 0 && (
                  <OptionsApprovalButton onClick={handleAddOption} section={section} padTop />
                )}
              </div>
              {/* A sibling block below the name/value row (not another flex child of `firstLine`) —
                  otherwise the multi-line description sits beside the name and, lacking `HtmlContent`'s
                  paragraph/pre-wrap handling, its line breaks collapse into one run-on line (ENG-1850). */}
              {subItem.name && subItem.description && (
                <div className={classes.descriptionWrapper}>
                  <div className={classes.description}>
                    <HtmlContent content={subItem.translatedDescription} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {hasName && hasDescription && (
        <div className={classes.descriptionWrapper}>
          <div className={classes.description}>
            <HtmlContent content={translatedDescription} />
          </div>
          {optionsApprovalPlacement === 'fourth' && (
            <OptionsApprovalButton onClick={handleAddOption} section={section} padTop />
          )}
        </div>
      )}

      {optionsApprovalPlacement === 'fifth' && (
        <div className={classes.justifyEnd}>
          <OptionsApprovalButton onClick={handleAddOption} section={section} padTop />
        </div>
      )}
    </div>
  )
}

// Memoized so parent re-renders that don't change this row's props (e.g. the ItemTable drag
// state toggling, or a sibling row's selection) skip re-rendering the item's content. `item`
// is referentially stable across such renders (memoized in the calling table), and the rest
// of the props are primitives. ItemColumn still re-renders on real quote-data changes because
// it consumes QuoteContext via useQuote(). (#21450)
export default React.memo(ItemColumn)
